// Chat state shape
const initialState = {
  conversations: [],
  activeConversation: null,
  messages: [],
  onlineUsers: [],
  typingUsers: {},
  unreadTotal: 0,
  loading: false,
  error: null,
};

// Action types
export const CHAT_ACTIONS = {
  SET_LOADING: "CHAT_SET_LOADING",
  SET_ERROR: "CHAT_SET_ERROR",
  SET_CONVERSATIONS: "CHAT_SET_CONVERSATIONS",
  ADD_CONVERSATION: "CHAT_ADD_CONVERSATION",
  UPDATE_CONVERSATION: "CHAT_UPDATE_CONVERSATION",
  SET_ACTIVE_CONVERSATION: "CHAT_SET_ACTIVE_CONVERSATION",
  SET_MESSAGES: "CHAT_SET_MESSAGES",
  ADD_MESSAGE: "CHAT_ADD_MESSAGE",
  UPDATE_MESSAGE: "CHAT_UPDATE_MESSAGE",
  DELETE_MESSAGE: "CHAT_DELETE_MESSAGE",
  SET_ONLINE_USERS: "CHAT_SET_ONLINE_USERS",
  USER_ONLINE: "CHAT_USER_ONLINE",
  USER_OFFLINE: "CHAT_USER_OFFLINE",
  SET_TYPING: "CHAT_SET_TYPING",
  CLEAR_TYPING: "CHAT_CLEAR_TYPING",
  MARK_AS_READ: "CHAT_MARK_AS_READ",
  RESET: "CHAT_RESET",
};

export function chatReducer(state = initialState, action) {
  switch (action.type) {
    case CHAT_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case CHAT_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case CHAT_ACTIONS.SET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload,
        unreadTotal: action.payload.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0
        ),
        loading: false,
      };

    case CHAT_ACTIONS.ADD_CONVERSATION:
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
      };

    case CHAT_ACTIONS.UPDATE_CONVERSATION: {
      const updatedConversations = state.conversations.map((conv) =>
        conv._id === action.payload._id ? { ...conv, ...action.payload } : conv
      );
      // Sort by updatedAt
      updatedConversations.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      return {
        ...state,
        conversations: updatedConversations,
        unreadTotal: updatedConversations.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0
        ),
      };
    }

    case CHAT_ACTIONS.SET_ACTIVE_CONVERSATION:
      return { ...state, activeConversation: action.payload };

    case CHAT_ACTIONS.SET_MESSAGES:
      return { ...state, messages: action.payload, loading: false };

    case CHAT_ACTIONS.ADD_MESSAGE: {
      // Check if message already exists
      const exists = state.messages.some(
        (msg) => msg._id === action.payload._id
      );
      if (exists) return state;

      return {
        ...state,
        messages: [...state.messages, action.payload],
      };
    }

    case CHAT_ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg._id === action.payload._id ? { ...msg, ...action.payload } : msg
        ),
      };

    case CHAT_ACTIONS.DELETE_MESSAGE:
      if (action.payload.forEveryone) {
        return {
          ...state,
          messages: state.messages.map((msg) =>
            msg._id === action.payload.messageId
              ? { ...msg, deletedForEveryone: true, text: "", media: [] }
              : msg
          ),
        };
      }
      return {
        ...state,
        messages: state.messages.filter(
          (msg) => msg._id !== action.payload.messageId
        ),
      };

    case CHAT_ACTIONS.SET_ONLINE_USERS:
      return { ...state, onlineUsers: action.payload };

    case CHAT_ACTIONS.USER_ONLINE:
      if (state.onlineUsers.includes(action.payload)) return state;
      return {
        ...state,
        onlineUsers: [...state.onlineUsers, action.payload],
      };

    case CHAT_ACTIONS.USER_OFFLINE:
      return {
        ...state,
        onlineUsers: state.onlineUsers.filter((id) => id !== action.payload),
      };

    case CHAT_ACTIONS.SET_TYPING:
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.conversationId]: action.payload.userName,
        },
      };

    case CHAT_ACTIONS.CLEAR_TYPING: {
      const newTypingUsers = { ...state.typingUsers };
      delete newTypingUsers[action.payload];
      return { ...state, typingUsers: newTypingUsers };
    }

    case CHAT_ACTIONS.MARK_AS_READ:
      return {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv._id === action.payload
            ? { ...conv, unreadCount: 0, isMarkedUnread: false }
            : conv
        ),
        unreadTotal: state.conversations.reduce(
          (sum, conv) =>
            sum + (conv._id === action.payload ? 0 : conv.unreadCount || 0),
          0
        ),
      };

    case CHAT_ACTIONS.RESET:
      return initialState;

    default:
      return state;
  }
}

export default chatReducer;
